// Apply-action guard chains — faithful ports of the Android register validations (tvDiscount /
// tvCoupons / tvOTDPrice handlers + validOtd/validUsePoint/validStoreCredit in OrderCreateViewModel).
// Each returns { ok:true } or { ok:false, message }. Evaluated in the SAME order as Android (first
// failing rule wins) so cashiers see the same message for the same situation.
//
// `cart` = webRegisterCart slice state. Helpers below read its current shape:
//   lines[], totals{subtotal,grandTotal,balanceDue}, coupon, couponTotal, customTotal,
//   isOutdoor, loyaltyAmount, storeCreditAmount, giftCards[], payments[].

import { toNum } from "./engine/money";
import { deziUpcSetOf, hasDeziItems } from "./engine/dezi";

const hasGiftCardRedeem = (cart) => (cart.giftCards || []).length > 0;
const isSplitInProgress = (cart) => (cart.payments || []).length > 0; // a tender already taken
const anyLine = (cart) => (cart.lines || []).length > 0;
const orderDiscountApplied = (cart) => toNum(cart.couponTotal) > 0 || toNum(cart.customTotal) > 0;
// Lottery branches (Android tvDiscount / tvCoupons / validOtd): lottery TICKETS never take an order
// discount, payout lines disable it outright. `nonPayout` = Android `filteredList`.
const isTicket = (l) => l.isLotteryTicket === "1";
const isPayout = (l) => !!(l.isLotteryPayout || l.isScratcherPayout);
const allTickets = (cart) => (cart.lines || []).length > 0 && (cart.lines || []).every(isTicket);
const allPayouts = (cart) => (cart.lines || []).length > 0 && (cart.lines || []).every(isPayout);
const nonPayoutAllTickets = (cart) => {
  const rest = (cart.lines || []).filter((l) => !isPayout(l));
  return rest.length > 0 && rest.every(isTicket);
};
const hasPayout = (cart) => (cart.lines || []).some(isPayout);

// ── Order-level discount (Discounts button) ──
export function validOrderDiscount(cart) {
  if (isSplitInProgress(cart)) return fail("Split payment in progress — discounts can't be changed");
  if (!anyLine(cart)) return fail("Cart is empty");
  if (toNum(cart.totals.subtotal) === 0) return fail("Subtotal is zero");
  if (cart.isOutdoor) return fail("Remove the OTD price first");
  if (toNum(cart.loyaltyAmount) !== 0) return fail("Discount must be applied before loyalty points");
  if (toNum(cart.storeCreditAmount) !== 0) return fail("Discount must be applied before store credit");
  if (hasGiftCardRedeem(cart)) return fail("Remove the gift card, then apply a discount");
  if (allTickets(cart)) return fail("Discounts cannot be applied to lottery products!");
  if (allPayouts(cart)) return fail("Discount is disabled for this transaction");
  if (nonPayoutAllTickets(cart)) return fail("Discounts cannot be applied to lottery products!");
  return ok();
}

// ── Coupon (Coupons button) ──
export function validCoupon(cart) {
  if (isSplitInProgress(cart)) return fail("Split payment in progress — coupons can't be changed");
  if (hasGiftCardRedeem(cart)) return fail("Remove the gift card, then apply a coupon");
  if (!anyLine(cart)) return fail("Cart is empty");
  // Android tvCoupons: one LIST coupon per order (mOrderDiscount is a coupon-list name). Custom $
  // discounts may still stack on top of it — only a second coupon is refused.
  if (cart.orderCouponModel && cart.orderCouponModel.isListCoupon)
    return fail(`${cart.orderCouponModel.name} Discount is already applied, please remove previous discount and then add new one.`);
  if (toNum(cart.totals.subtotal) === 0) return fail("Subtotal is zero");
  if (cart.isOutdoor) return fail("Remove the OTD price first");
  if (toNum(cart.loyaltyAmount) !== 0) return fail("Coupon must be applied before loyalty points");
  if (toNum(cart.storeCreditAmount) !== 0) return fail("Coupon must be applied before store credit");
  if (allTickets(cart)) return fail("Coupon cannot be applied to lottery products!");
  if (allPayouts(cart)) return fail("Coupon is disabled for this transaction");
  return ok();
}

// OTD button open-guard — the amount-independent subset of validOtd (the web OTD modal has its own
// amount field, so amount rules are checked on apply, not on open).
export function validOtdOpen(cart) {
  if (!anyLine(cart)) return fail("Cart is empty");
  if (isSplitInProgress(cart)) return fail("Split payment in progress — can't set an OTD price");
  if (orderDiscountApplied(cart)) return fail("Kindly remove the discount first");
  if (hasGiftCardRedeem(cart)) return fail("Remove the gift card, then set an OTD price");
  if (toNum(cart.loyaltyAmount) !== 0) return fail("OTD must be applied before loyalty points");
  // QV-7124 rule 5 — OTD is mechanically an ORDER-LEVEL discount, so rule 4 (never stack on a
  // manufacturer-discounted item) applies to it. On a cart containing a dezi line the eligible base
  // collapses to the non-dezi portion, which OTD's contract ("the customer pays exactly $X out the
  // door") cannot express — and on an ALL-dezi cart the base is $0, which on Android produced a
  // $0 discount line that was pruned while the OTD flag stayed set, wedging the register.
  // Cart-scoped by design: with no dezi LINE present the base is unchanged, so OTD is allowed even if
  // a previous lookup left dezi upcs known.
  // Accepted product cost (owner-confirmed on Android): to use OTD the cashier must remove the
  // manufacturer-discounted item, or forgo OTD.
  if (hasDeziItems(cart.lines || [], deziUpcSetOf(cart.deziDiscounts)))
    return fail("OTD can't be applied with a manufacturer discount.");
  if (hasPayout(cart)) return fail("Remove Lottery and Scratcher Payout to use OTD!");
  if (allTickets(cart)) return fail("OTD cannot be applied to lottery products!");
  return ok();
}

// ── Add Customer (addCustomerValidView) ──
// Android requires ALL THREE: first name, a valid mobile number, a valid email. Last name is
// optional. Evaluated in Android's order so the first complaint matches.
export function validNewCustomer({ firstName, phone, email }) {
  if (!String(firstName || "").trim()) return fail("Enter first name");
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return fail("Enter mobile number");
  // Android phone_number_length = 10 (number field, maxLength 10) → must be EXACTLY 10 digits.
  if (digits.length !== 10) return fail("Enter a valid mobile number");
  if (!String(email || "").trim()) return fail("Enter email address");
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email).trim())) return fail("Enter a valid email address");
  return ok();
}

// ── Quick-Add product (newQuickAddValidView) ──
// Android requires name, a price above zero, and a UPC. The UPC is mandatory precisely BECAUSE the
// product must be scannable afterwards — and it is what lets a quick-add product be matched by
// manufacturer-discount lookups like any other catalog line.
export function validQuickAddProduct({ title, price, upc }) {
  if (!String(title || "").trim()) return fail("Enter product name");
  if (!(toNum(price) > 0)) return fail("Enter selling price");
  if (!String(upc || "").trim()) return fail("Enter UPC code");
  return ok();
}

// ── OTD price (validOtd) ──
export function validOtd(cart, inputAmount) {
  if (!anyLine(cart)) return fail("Cart is empty");
  if (toNum(inputAmount) === 0) return fail("Enter an amount");
  if (isSplitInProgress(cart)) return fail("Split payment in progress — can't set an OTD price");
  if (orderDiscountApplied(cart)) return fail("Kindly remove the discount first");
  if (hasGiftCardRedeem(cart)) return fail("Remove the gift card, then set an OTD price");
  if (toNum(cart.loyaltyAmount) !== 0) return fail("OTD must be applied before loyalty points");
  if (toNum(inputAmount) >= toNum(cart.totals.grandTotal)) return fail("Entered amount must be less than the total");
  if (hasPayout(cart)) return fail("Remove Lottery and Scratcher Payout to use OTD!");
  if (allTickets(cart)) return fail("OTD cannot be applied to lottery products!");
  return ok();
}

// ── Lottery / Scratcher payout (validLottery) ──
export function validLottery(cart, amount) {
  if (toNum(amount) === 0) return fail("Enter Amount");
  if (hasGiftCardRedeem(cart)) return fail("Remove the gift card first");
  if (cart.isOutdoor) return fail("Remove the OTD price first");
  if (isSplitInProgress(cart)) return fail("Split payment in progress");
  return ok();
}

// ── Loyalty (validUsePoint) ──
export function validLoyalty(cart, { hasCustomer, available }) {
  if (toNum(cart.totals.balanceDue) <= 0) return fail("Loyalty is disabled for this transaction");
  // Android: getSumOfLotteryAndScratchAmt() > getLotteryGrandValue() — a payout larger than the sale
  // (subtotal − loyalty + tax − order discounts − store credit) disables points.
  {
    const payouts = (cart.lines || []).reduce((s, l) => (l.isLotteryPayout || l.isScratcherPayout ? s + (toNum(l.orderUnitNewPrice) > 0 ? toNum(l.orderUnitNewPrice) : toNum(l.orderUnitPrice)) * toNum(l.orderCount) : s), 0);
    const t = cart.totals || {};
    const lotteryGrand = toNum(t.subtotal) - toNum(cart.loyaltyAmount) + toNum(t.taxRateInAmount) - toNum(cart.couponTotal) - toNum(cart.customTotal) - toNum(cart.storeCreditAmount);
    if (payouts > 0 && payouts > lotteryGrand) return fail("Loyalty is disabled for this transaction");
  }
  if (!hasCustomer) return fail("Add a customer to use points");
  if (isSplitInProgress(cart)) return fail("Split payment in progress — can't apply loyalty");
  if (hasGiftCardRedeem(cart)) return fail("Remove the gift card, then apply loyalty");
  if (toNum(cart.storeCreditAmount) !== 0) return fail("Remove store credit, then apply loyalty");
  if (toNum(cart.loyaltyAmount) !== 0) return fail("Loyalty already applied");
  if (!available || toNum(available) <= 0) return fail("Loyalty is not available");
  if (cart.isOutdoor) return fail("Remove the OTD price first");
  return ok();
}

// ── Store credit (validStoreCredit) ──
export function validStoreCredit(cart, { hasCustomer, available }) {
  if (!anyLine(cart)) return fail("Cart is empty");
  if (!hasCustomer) return fail("Add a customer to use store credit");
  if (!available || toNum(available) === 0) return fail("Store credit not found");
  if (toNum(cart.totals.balanceDue) <= 0) return fail("Store credit is disabled for this transaction");
  if (isSplitInProgress(cart)) return fail("Split payment in progress — can't apply store credit");
  if (toNum(cart.storeCreditAmount) !== 0) return fail("Store credit already applied");
  return ok();
}

function ok() {
  return { ok: true };
}
function fail(message) {
  return { ok: false, message };
}
